import React, { useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Text,
  Flex,
  Grid,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Image,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import { AddIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';
import { ModeSelector } from '../components/campaign/ModeSelector';
import { campaignService } from '../services/api/CampaignService';
import type { CampaignContentSnapshot } from '../components/campaign/CampaignContentSummary';

/**
 * Campaign builder for authenticated campaign creators.
 *
 * WHO: A creator enters campaign metadata and chooses authored setting/adventure content.
 * WHAT: Quick and full paths validate required content and submit one CampaignCreateRequest.
 * WHERE: Mounted from App.tsx; CampaignService.createCampaign persists campaign_settings,
 *        and CampaignPage later reads that server-returned JSON through CampaignContentSummary.
 * WHEN: All wizard state is local and remains unsaved until the explicit Create Campaign action.
 *       The single create request is awaited before success navigation; failures leave the form
 *       state available for correction/retry, and the loading guard prevents duplicate submits.
 * WHY: PRD004.6 requires reentry-safe selected content without a separate draft/catalog service.
 *      Versioned stable IDs and provenance accompany the content snapshot in existing JSON.
 *      Only short original prompts are selectable; published-book cards stay disabled because
 *      this app does not bundle those works. No invite, session, voice, or draft resource is made.
 *
 * See shared/docs/Prompts/004_Campaign_Character_Session_Core_Flows_TODO_PRD.md (004.6).
 */

// Step Indicator Component
interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  onStepClick: (step: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ 
  currentStep, 
  totalSteps, 
  onStepClick 
}) => {
  return (
    <Flex justify="center" mb={8}>
      {Array.from({ length: totalSteps }).map((_, index) => (
        <React.Fragment key={index}>
          <Box
            w="40px"
            h="40px"
            borderRadius="full"
            bg={index < currentStep ? mythboundTheme.colors.primary.main : 'rgba(59, 232, 255, 0.1)'}
            color={index < currentStep ? mythboundTheme.colors.primary.dark : mythboundTheme.colors.secondary.white}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            cursor="pointer"
            onClick={() => onStepClick(index + 1)}
            boxShadow={index === currentStep - 1 ? mythboundTheme.shadows.glow : 'none'}
            transition="all 0.3s"
          >
            {index + 1}
          </Box>
          {index < totalSteps - 1 && (
            <Box
              h="2px"
              w="60px"
              bg={index < currentStep - 1 ? mythboundTheme.colors.primary.main : 'rgba(59, 232, 255, 0.1)'}
              alignSelf="center"
            />
          )}
        </React.Fragment>
      ))}
    </Flex>
  );
};

// ResourceCard is keyboard-operable for supported choices. Disabled published-content cards
// are explanatory only and cannot change the campaign snapshot (PRD004.6; no bundled catalog).
// Resource Card Component
interface ResourceCardProps {
  title: string;
  description: string;
  image?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isSelected?: boolean;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ 
  title, 
  description, 
  image, 
  onClick,
  isDisabled = false,
  isSelected = false,
}) => {
  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      overflow="hidden"
      bg={mythboundTheme.colors.secondary.parchment}
      color={mythboundTheme.colors.accent.brown}
      transition="all 0.3s"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: mythboundTheme.shadows.goldGlow,
      }}
      onClick={isDisabled ? undefined : onClick}
      role={onClick || isDisabled ? 'button' : undefined}
      tabIndex={onClick && !isDisabled ? 0 : undefined}
      aria-disabled={isDisabled || undefined}
      aria-pressed={onClick && !isDisabled ? isSelected : undefined}
      onKeyDown={(event) => {
        if (!isDisabled && onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
      cursor={isDisabled ? 'not-allowed' : onClick ? 'pointer' : 'default'}
      opacity={isDisabled ? 0.55 : 1}
      borderColor={isSelected ? mythboundTheme.colors.primary.main : mythboundTheme.colors.primary.gold}
    >
      {image && (
        <Box h="140px" overflow="hidden">
          <Image 
            src={image} 
            alt={title} 
            objectFit="cover" 
            w="100%" 
            h="100%" 
            // Fixed 2026-02-26: replaced via.placeholder.com (ERR_NAME_NOT_RESOLVED) with inline data URI
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='140'%3E%3Crect width='300' height='140' fill='%23333'/%3E%3Ctext x='150' y='75' text-anchor='middle' fill='%23888' font-family='sans-serif' font-size='14'%3EMythbound%3C/text%3E%3C/svg%3E"
          />
        </Box>
      )}
      <Box p={4}>
        <Heading 
          size="sm" 
          mb={2}
          fontFamily={mythboundTheme.typography.fontFamily.heading}
        >
          {title}
        </Heading>
        <Text fontSize="sm">{description}</Text>
      </Box>
    </Box>
  );
};

// Main Campaign Builder Component
export const CampaignBuilder: React.FC = () => {
  // Mode selection state (step 0)
  const [showModeSelector, setShowModeSelector] = useState(true);
  const [isQuickMode, setIsQuickMode] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalSteps = 4;
  const toast = useToast();
  const navigate = useNavigate();
  
  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [campaignDescription, setCampaignDescription] = useState('');
  const [campaignSetting, setCampaignSetting] = useState('');
  const [selectedSettingId, setSelectedSettingId] = useState('');
  const [selectedAdventureId, setSelectedAdventureId] = useState('mythbound.lantern-road');
  const [customAdventureTitle, setCustomAdventureTitle] = useState('');
  const [customAdventureHook, setCustomAdventureHook] = useState('');
  const [customAdventureAntagonist, setCustomAdventureAntagonist] = useState('');
  const [campaignLevel, setCampaignLevel] = useState('1');
  const [campaignVisibility, setCampaignVisibility] = useState<'public' | 'private' | 'unlisted'>('private');
  
  // Handle step navigation
  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };
  
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Submit form
      handleSubmit();
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Handle mode selection
  const handleModeSelect = (quick: boolean) => {
    setIsQuickMode(quick);
    setShowModeSelector(false);
  };
  
  // Handle back to mode selector
  const handleBackToModeSelector = () => {
    setShowModeSelector(true);
    setCurrentStep(1);
    // Reset form
    setCampaignName('');
    setCampaignDescription('');
    setCampaignSetting('');
    setSelectedSettingId('');
    setSelectedAdventureId('mythbound.lantern-road');
    setCustomAdventureTitle('');
    setCustomAdventureHook('');
    setCustomAdventureAntagonist('');
    setCampaignLevel('1');
    setCampaignVisibility('private');
  };
  
  // ---------------------------------------------------------------------------
  // Computed: form validity check for both quick and full wizard modes
  // Updated 2026-02-23: Disable submit when name or description is empty
  // ---------------------------------------------------------------------------
  const isCustomAdventure = selectedAdventureId === 'custom';
  const isFormValid = campaignName.trim().length > 0 && campaignDescription.trim().length > 0 &&
    campaignSetting.trim().length > 0 &&
    (!isCustomAdventure || (customAdventureTitle.trim().length > 0 && customAdventureHook.trim().length > 0));

  // Handle form submission - calls CampaignService.createCampaign()
  // Updated 2026-02-23: Toast spec (success 3s top-right, error 5s top-right),
  // redirect to new campaign page, FUTURE comment for scroll animation
  const handleSubmit = async () => {
    if (!isFormValid) {
      toast({
        title: 'Validation Error',
        description: 'Campaign name, description, setting, and a complete adventure selection are required.',
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    // The payload is assembled only after explicit Create. It stores immutable versioned
    // display snapshots in the existing campaign_settings field; no draft or resource row
    // is created while the user moves through the wizard.
    const starterSetting = selectedSettingId === 'mythbound.lantern-coast';
    const content: CampaignContentSnapshot = {
      schema_version: 1,
      setting: {
        id: starterSetting ? 'mythbound.lantern-coast' : 'custom-setting',
        version: 1,
        provenance: starterSetting ? 'mythbound-original' : 'user-authored',
        title: starterSetting ? 'The Lantern Coast' : 'Custom setting',
        description: campaignSetting.trim(),
      },
      adventure: isCustomAdventure ? {
        id: 'custom-adventure',
        version: 1,
        provenance: 'user-authored',
        title: customAdventureTitle.trim(),
        hook: customAdventureHook.trim(),
        ...(customAdventureAntagonist.trim() ? { antagonist: customAdventureAntagonist.trim() } : {}),
      } : {
        id: 'mythbound.lantern-road',
        version: 1,
        provenance: 'mythbound-original',
        title: 'Lantern Road',
        hook: 'The coast’s warning beacons have gone dark. Escort a keeper’s apprentice along the cliff road and discover why the next signal never came.',
        antagonist: 'A smuggling crew using the dark beacons to move through the coves.',
      },
    };

    try {
      const campaign = await campaignService.createCampaign({
        campaign_name: campaignName,
        description: campaignDescription,
        game_system: 'dnd5e',
        campaign_settings: {
          setting: campaignSetting,
          level: campaignLevel,
          visibility: campaignVisibility,
          mode: isQuickMode ? 'quick' : 'full',
          content,
        },
      });

      toast({
        title: 'Campaign Created',
        description: `Your campaign "${campaign.campaign_name}" has been created successfully.`,
        status: 'success',
        duration: 3000,
        position: 'top-right',
        isClosable: true,
      });

      // FUTURE: Scroll roll-up animation on successful campaign creation

      // Navigate to the new campaign page after successful creation
      navigate(`/campaigns/${campaign.campaign_id}`);
    } catch (error: any) {
      console.error('Failed to create campaign:', error);
      toast({
        title: 'Campaign Creation Failed',
        description: error.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 5000,
        position: 'top-right',
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Only original short-form prompts are selectable. Published settings/adventures are
  // shown as unavailable because the app does not bundle or license their source material.
  const settingResources = [
    { id: 'mythbound.lantern-coast', title: 'The Lantern Coast', description: 'An original prompt: a storm-battered coast where beacon keepers guide ships past cliffs, coves, and half-forgotten ruins.', isSupported: true },
    { id: 'unsupported.forgotten-realms', title: 'Forgotten Realms', description: 'Published setting content is not bundled. Add your own licensed notes in the setting field.', isSupported: false },
    { id: 'unsupported.eberron', title: 'Eberron', description: 'Published setting content is not bundled. Add your own licensed notes in the setting field.', isSupported: false },
    { id: 'unsupported.ravenloft', title: 'Ravenloft', description: 'Published setting content is not bundled. Add your own licensed notes in the setting field.', isSupported: false },
    { id: 'unsupported.theros', title: 'Theros', description: 'Published setting content is not bundled. Add your own licensed notes in the setting field.', isSupported: false },
  ];

  const adventureResources = [
    { id: 'mythbound.lantern-road', title: 'Lantern Road', description: 'Original short adventure: escort a beacon keeper’s apprentice along a cliff road after the coast’s warning lights go dark.', isSupported: true },
    { id: 'unsupported.lost-mine', title: 'Lost Mine of Phandelver', description: 'Published adventure content is not bundled. Choose Custom Adventure or use your own licensed materials.', isSupported: false },
    { id: 'unsupported.curse-of-strahd', title: 'Curse of Strahd', description: 'Published adventure content is not bundled. Choose Custom Adventure or use your own licensed materials.', isSupported: false },
    { id: 'unsupported.dragon-heist', title: 'Dragon Heist', description: 'Published adventure content is not bundled. Choose Custom Adventure or use your own licensed materials.', isSupported: false },
  ];

  return (
    <Box>
      <Heading 
        mb={6} 
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.primary.gold}
      >
        Campaign Builder
      </Heading>
      
      {/* Mode Selector (Step 0) */}
      {showModeSelector && (
        <Box
          bg="rgba(17, 17, 22, 0.7)"
          borderRadius="md"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.brown}
          p={6}
          mb={8}
        >
          <ModeSelector 
            isQuickMode={isQuickMode} 
            onModeChange={handleModeSelect} 
          />
        </Box>
      )}
      
      {/* Quick Create Form */}
      {!showModeSelector && isQuickMode && (
        <Box
          bg="rgba(17, 17, 22, 0.7)"
          borderRadius="md"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.brown}
          p={6}
          mb={8}
        >
          <Heading 
            size="md" 
            mb={6}
            fontFamily={mythboundTheme.typography.fontFamily.heading}
            color={mythboundTheme.colors.secondary.white}
            textAlign="center"
          >
            Quick Campaign Creation
          </Heading>
          
          <VStack spacing={6} align="stretch" maxW="600px" mx="auto">
            <FormControl isRequired>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Campaign Name</FormLabel>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Enter a name for your campaign"
                bg="rgba(245, 245, 247, 0.05)"
                borderColor={mythboundTheme.colors.accent.silver}
                color={mythboundTheme.colors.secondary.white}
                _hover={{ borderColor: mythboundTheme.colors.primary.main }}
                _focus={{
                  borderColor: mythboundTheme.colors.primary.main,
                  boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                }}
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Description</FormLabel>
              <Textarea
                value={campaignDescription}
                onChange={(e) => setCampaignDescription(e.target.value)}
                placeholder="Describe your campaign's story and theme"
                bg="rgba(245, 245, 247, 0.05)"
                borderColor={mythboundTheme.colors.accent.silver}
                color={mythboundTheme.colors.secondary.white}
                _hover={{ borderColor: mythboundTheme.colors.primary.main }}
                _focus={{
                  borderColor: mythboundTheme.colors.primary.main,
                  boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                }}
                rows={4}
              />
            </FormControl>
            
            <FormControl isRequired>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Visibility</FormLabel>
              <RadioGroup value={campaignVisibility} onChange={(val) => setCampaignVisibility(val as 'public' | 'private' | 'unlisted')}>
                <Stack direction="row" spacing={4}>
                  <Radio value="private" colorScheme="blue">Private</Radio>
                  <Radio value="unlisted" colorScheme="blue">Unlisted</Radio>
                  <Radio value="public" colorScheme="blue">Public</Radio>
                </Stack>
              </RadioGroup>
              <Text fontSize="sm" color={mythboundTheme.colors.accent.silver} mt={2}>
                {campaignVisibility === 'private' && 'Only you and invited players can see this campaign.'}
                {campaignVisibility === 'unlisted' && 'Anyone with the link can join.'}
                {campaignVisibility === 'public' && 'Anyone can find and join this campaign.'}
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Campaign Setting</FormLabel>
              <Textarea value={campaignSetting} onChange={(e) => { setCampaignSetting(e.target.value); setSelectedSettingId('custom'); }} placeholder="Describe your world, or choose the original Lantern Coast prompt in the full builder." rows={3} />
            </FormControl>
            <FormControl>
              <FormLabel color={mythboundTheme.colors.secondary.white}>Starting Adventure</FormLabel>
              <Select value={selectedAdventureId} onChange={(e) => setSelectedAdventureId(e.target.value)} bg="rgba(245, 245, 247, 0.05)" color={mythboundTheme.colors.secondary.white}>
                <option value="mythbound.lantern-road">Lantern Road — original starter</option>
                <option value="custom">Custom adventure</option>
              </Select>
            </FormControl>
            {isCustomAdventure && (
              <>
                <FormControl isRequired>
                  <FormLabel color={mythboundTheme.colors.secondary.white}>Adventure Title</FormLabel>
                  <Input value={customAdventureTitle} onChange={(e) => setCustomAdventureTitle(e.target.value)} />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel color={mythboundTheme.colors.secondary.white}>Adventure Hook</FormLabel>
                  <Textarea value={customAdventureHook} onChange={(e) => setCustomAdventureHook(e.target.value)} rows={3} />
                </FormControl>
              </>
            )}
          </VStack>
          
          <Flex justify="space-between" mt={8}>
            <Button 
              leftIcon={<ChevronLeftIcon />} 
              onClick={handleBackToModeSelector}
              bg={mythboundTheme.colors.accent.brown}
              color={mythboundTheme.colors.secondary.white}
              _hover={{ bg: mythboundTheme.colors.primary.dark }}
            >
              Back to Mode Selection
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!isFormValid}
              isLoading={isSubmitting}
              loadingText="Creating..."
            >
              Create Campaign
            </Button>
          </Flex>
        </Box>
      )}
      
      {/* Full Wizard */}
      {!showModeSelector && !isQuickMode && (
      <Box
        bg="rgba(17, 17, 22, 0.7)"
        borderRadius="md"
        borderWidth="1px"
        borderColor={mythboundTheme.colors.accent.brown}
        p={6}
        mb={8}
      >
        <StepIndicator 
          currentStep={currentStep} 
          totalSteps={totalSteps} 
          onStepClick={goToStep} 
        />
        
        {/* Step 1: Campaign Basics */}
        {currentStep === 1 && (
          <Box>
            <Heading 
              size="md" 
              mb={6}
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.secondary.white}
              textAlign="center"
            >
              Campaign Basics
            </Heading>
            
            <VStack spacing={6} align="stretch" maxW="600px" mx="auto">
              <FormControl isRequired>
                <FormLabel color={mythboundTheme.colors.secondary.white}>Campaign Name</FormLabel>
                <Input
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Enter a name for your campaign"
                  bg="rgba(245, 245, 247, 0.05)"
                  borderColor={mythboundTheme.colors.accent.silver}
                  color={mythboundTheme.colors.secondary.white}
                  _hover={{
                    borderColor: mythboundTheme.colors.primary.main,
                  }}
                  _focus={{
                    borderColor: mythboundTheme.colors.primary.main,
                    boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                  }}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel color={mythboundTheme.colors.secondary.white}>Description</FormLabel>
                <Textarea
                  value={campaignDescription}
                  onChange={(e) => setCampaignDescription(e.target.value)}
                  placeholder="Describe your campaign's story and theme"
                  bg="rgba(245, 245, 247, 0.05)"
                  borderColor={mythboundTheme.colors.accent.silver}
                  color={mythboundTheme.colors.secondary.white}
                  _hover={{
                    borderColor: mythboundTheme.colors.primary.main,
                  }}
                  _focus={{
                    borderColor: mythboundTheme.colors.primary.main,
                    boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                  }}
                  rows={4}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel color={mythboundTheme.colors.secondary.white}>Starting Level</FormLabel>
                <Select
                  value={campaignLevel}
                  onChange={(e) => setCampaignLevel(e.target.value)}
                  bg="rgba(245, 245, 247, 0.05)"
                  borderColor={mythboundTheme.colors.accent.silver}
                  color={mythboundTheme.colors.secondary.white}
                  _hover={{
                    borderColor: mythboundTheme.colors.primary.main,
                  }}
                  _focus={{
                    borderColor: mythboundTheme.colors.primary.main,
                    boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                  }}
                >
                  {Array.from({ length: 20 }).map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Level {i + 1}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </Box>
        )}
        
        {/* Step 2: World Setting */}
        {currentStep === 2 && (
          <Box>
            <Heading 
              size="md" 
              mb={6}
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.secondary.white}
              textAlign="center"
            >
              World Setting
            </Heading>
            
            <VStack spacing={6} align="stretch" maxW="800px" mx="auto">
              <FormControl isRequired>
                <FormLabel color={mythboundTheme.colors.secondary.white}>Setting</FormLabel>
                <Textarea
                  value={campaignSetting}
                  onChange={(e) => { setCampaignSetting(e.target.value); setSelectedSettingId('custom'); }}
                  placeholder="Describe the world where your campaign takes place"
                  bg="rgba(245, 245, 247, 0.05)"
                  borderColor={mythboundTheme.colors.accent.silver}
                  color={mythboundTheme.colors.secondary.white}
                  _hover={{
                    borderColor: mythboundTheme.colors.primary.main,
                  }}
                  _focus={{
                    borderColor: mythboundTheme.colors.primary.main,
                    boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                  }}
                  rows={4}
                />
              </FormControl>
              
              <Box>
                <Heading 
                  size="sm" 
                  mb={3}
                  fontFamily={mythboundTheme.typography.fontFamily.heading}
                  color={mythboundTheme.colors.primary.main}
                >
                  Setting Templates
                </Heading>
                <Text fontSize="sm" color={mythboundTheme.colors.secondary.white} mb={4}>
                  Choose the original Lantern Coast prompt, use your own setting notes, or review unavailable published settings.
                </Text>
                
                <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4}>
                  {settingResources.map((resource) => (
                    <ResourceCard
                      key={resource.id}
                      title={resource.title}
                      description={resource.description}
                      isDisabled={!resource.isSupported}
                      isSelected={selectedSettingId === resource.id}
                      onClick={resource.isSupported ? () => {
                        setSelectedSettingId(resource.id);
                        setCampaignSetting(resource.description);
                      } : undefined}
                    />
                  ))}
                </Grid>
              </Box>
            </VStack>
          </Box>
        )}
        
        {/* Step 3: Adventure Setup */}
        {currentStep === 3 && (
          <Box>
            <Heading 
              size="md" 
              mb={6}
              fontFamily={mythboundTheme.typography.fontFamily.heading}
              color={mythboundTheme.colors.secondary.white}
              textAlign="center"
            >
              Adventure Setup
            </Heading>
            
            <VStack spacing={6} align="stretch" maxW="800px" mx="auto">
              <Tabs index={isCustomAdventure ? 1 : 0} variant="soft-rounded" colorScheme="blue" onChange={(index) => setSelectedAdventureId(index === 1 ? 'custom' : 'mythbound.lantern-road')}>
                <TabList mb={4} justifyContent="center">
                  <Tab 
                    color={mythboundTheme.colors.secondary.white}
                    _selected={{ 
                      color: mythboundTheme.colors.primary.dark,
                      bg: mythboundTheme.colors.primary.main,
                    }}
                  >
                    Pre-made Adventures
                  </Tab>
                  <Tab 
                    color={mythboundTheme.colors.secondary.white}
                    _selected={{ 
                      color: mythboundTheme.colors.primary.dark,
                      bg: mythboundTheme.colors.primary.main,
                    }}
                  >
                    Custom Adventure
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0}>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4}>
                      {adventureResources.map((resource) => (
                        <ResourceCard
                          key={resource.id}
                          title={resource.title}
                          description={resource.description}
                          isDisabled={!resource.isSupported}
                          isSelected={selectedAdventureId === resource.id}
                          onClick={resource.isSupported ? () => setSelectedAdventureId(resource.id) : undefined}
                        />
                      ))}
                    </Grid>
                  </TabPanel>
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired>
                        <FormLabel color={mythboundTheme.colors.secondary.white}>Adventure Title</FormLabel>
                        <Input
                          value={customAdventureTitle}
                          onChange={(e) => { setCustomAdventureTitle(e.target.value); setSelectedAdventureId('custom'); }}
                          placeholder="Enter a title for your adventure"
                          bg="rgba(245, 245, 247, 0.05)"
                          borderColor={mythboundTheme.colors.accent.silver}
                          color={mythboundTheme.colors.secondary.white}
                          _hover={{
                            borderColor: mythboundTheme.colors.primary.main,
                          }}
                          _focus={{
                            borderColor: mythboundTheme.colors.primary.main,
                            boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                          }}
                        />
                      </FormControl>
                      
                      <FormControl isRequired>
                        <FormLabel color={mythboundTheme.colors.secondary.white}>Adventure Hook</FormLabel>
                        <Textarea
                          value={customAdventureHook}
                          onChange={(e) => { setCustomAdventureHook(e.target.value); setSelectedAdventureId('custom'); }}
                          placeholder="What draws the characters into this adventure?"
                          bg="rgba(245, 245, 247, 0.05)"
                          borderColor={mythboundTheme.colors.accent.silver}
                          color={mythboundTheme.colors.secondary.white}
                          _hover={{
                            borderColor: mythboundTheme.colors.primary.main,
                          }}
                          _focus={{
                            borderColor: mythboundTheme.colors.primary.main,
                            boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                          }}
                          rows={3}
                        />
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel color={mythboundTheme.colors.secondary.white}>Main Villain/Antagonist (optional)</FormLabel>
                        <Input
                          value={customAdventureAntagonist}
                          onChange={(e) => { setCustomAdventureAntagonist(e.target.value); setSelectedAdventureId('custom'); }}
                          placeholder="Who or what opposes the characters?"
                          bg="rgba(245, 245, 247, 0.05)"
                          borderColor={mythboundTheme.colors.accent.silver}
                          color={mythboundTheme.colors.secondary.white}
                          _hover={{
                            borderColor: mythboundTheme.colors.primary.main,
                          }}
                          _focus={{
                            borderColor: mythboundTheme.colors.primary.main,
                            boxShadow: `0 0 0 1px ${mythboundTheme.colors.primary.main}`,
                          }}
                        />
                      </FormControl>
                      
                      <Button 
                        leftIcon={<AddIcon />} 
                        variant="secondary" 
                        alignSelf="flex-start"
                        isDisabled
                        title="Separate location editing is not available; include location notes in the adventure hook."
                      >
                        Add Location
                      </Button>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          </Box>
        )}
        
        {/* No invitation draft is submitted by this builder. Invite actions stay on the
            persisted campaign page so a failed create cannot imply invitations were sent. */}
        {currentStep === 4 && (
          <Box>
            <Heading size="md" mb={6} color={mythboundTheme.colors.secondary.white}>Review and Create</Heading>
            <Text color={mythboundTheme.colors.accent.silver} mb={3}>
              {campaignName} — {campaignDescription}
            </Text>
            <Text color={mythboundTheme.colors.accent.silver}>
              Create saves your selected setting and adventure. Invite players from the campaign page after creation.
            </Text>
          </Box>
        )}

        {/* Navigation Buttons */}
        <Flex justify="space-between" mt={10}>
          {currentStep === 1 ? (
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={handleBackToModeSelector}
            >
              Back to Mode Selection
            </Button>
          ) : (
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={goToPreviousStep}
            >
              Previous
            </Button>
          )}
          
          {/* Updated 2026-02-23: Disable "Create Campaign" when name/description empty */}
          <Button
            rightIcon={currentStep < totalSteps ? <ChevronRightIcon /> : undefined}
            variant="primary"
            onClick={goToNextStep}
            isLoading={isSubmitting}
            loadingText="Creating..."
            isDisabled={currentStep === totalSteps && !isFormValid}
          >
            {currentStep < totalSteps ? 'Next' : 'Create Campaign'}
          </Button>
        </Flex>
      </Box>
      )}
      {/* End Full Wizard */}
      

    </Box>
  );
};

export default CampaignBuilder;
